import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { Shield, Building2 } from 'lucide-react';

export default function Leadership() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && (!user || user.SPESHELBOI !== 'ACCESS_GRANTED')) {
      navigate('/');
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!user || user.SPESHELBOI !== 'ACCESS_GRANTED') {
    return null;
  }

  const team = [
    {
      name: 'Sarah Chen',
      role: 'Chief Executive Officer',
      image: 'https://images.unsplash.com/photo-1758518729929-8210d3b0839e?w=400',
      bio: '15+ years in executive search and talent strategy'
    },
    {
      name: 'Michael Torres',
      role: 'Head of Permanent Placement',
      image: 'https://ui-avatars.com/api/?name=Michael+Torres&background=3b82f6&color=fff&size=400',
      bio: 'Specialist in building high-performance teams'
    },
    {
      name: 'Jessica Williams',
      role: 'Director of Contract Solutions',
      image: 'https://ui-avatars.com/api/?name=Jessica+Williams&background=10b981&color=fff&size=400',
      bio: 'Expert in flexible staffing and workforce optimization'
    },
    {
      name: 'David Park',
      role: 'Lead Consultant - Technology',
      image: 'https://ui-avatars.com/api/?name=David+Park&background=14b8a6&color=fff&size=400',
      bio: 'Connecting tech talent with innovative companies'
    }
  ];

  const partnerships = [
    {
      name: 'Deloitte',
      logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/56/Deloitte.svg/320px-Deloitte.svg.png',
      description: 'Strategic consulting partnership'
    },
    {
      name: 'DE Shaw',
      logo: 'https://ui-avatars.com/api/?name=DE+Shaw&background=1e3a8a&color=fff&size=200&bold=true',
      description: 'Technology talent acquisition'
    },
    {
      name: 'KKR',
      logo: 'https://ui-avatars.com/api/?name=KKR&background=059669&color=fff&size=200&bold=true',
      description: 'Executive search partnership'
    },
    {
      name: 'Oaktree',
      logo: 'https://ui-avatars.com/api/?name=Oaktree&background=7c3aed&color=fff&size=200&bold=true',
      description: 'Financial services recruitment'
    },
    {
      name: 'CDK Global',
      logo: 'https://ui-avatars.com/api/?name=CDK&background=dc2626&color=fff&size=200&bold=true',
      description: 'Technology solutions partnership'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Access Indicator */}
      <div className="bg-blue-600 text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center gap-2 text-sm">
          <Building2 size={16} />
          <span>Partner Access Area - Leadership & Partnerships</span>
        </div>
      </div>

      {/* Meet Our Leadership */}
      <section className="pt-24 pb-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 
              className="text-4xl font-bold text-gray-900 mb-4"
              data-anim="typing"
              data-speed="35"
            >
              Meet Our Leadership
            </h1>
            <p 
              className="text-lg text-gray-600 max-w-3xl mx-auto"
              data-anim="fade-up"
              data-delay="120"
            >
              Experienced professionals dedicated to <span
                data-anim="rotating-text"
                data-words="your success|exceptional results|strategic growth|talent excellence|business transformation"
                data-speed="3000"
                data-transition="500"
                className="font-semibold text-transparent text-2xl bg-clip-text bg-[linear-gradient(90deg,#ff6ec4,#7873f5,#4ade80,#06b6d4,#ff6ec4)] bg-[length:200%_100%] animate-[gradient-move_3s_linear_infinite]"
              >your success</span>.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-anim="list-stagger" data-stagger="120">
            {team.map((member, index) => (
              <Card key={index} className="hover:shadow-xl hover:border-blue-500/50 transition-all border-none">
                <CardContent className="p-0">
                  <img 
                    src={member.image} 
                    alt={member.name} 
                    className="w-full h-64 object-cover rounded-t-lg"
                    loading="lazy"
                  />
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-1">{member.name}</h3>
                    <p className="text-blue-600 font-medium mb-3 text-sm">{member.role}</p>
                    <p className="text-gray-600 text-sm">{member.bio}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Partnerships */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-4xl font-bold text-gray-900 mb-4 flex items-center justify-center gap-3"
              data-anim="fade-up"
            >
              <Building2 className="text-blue-600" size={40} />
              Our Strategic Partnerships
            </h2>
            <p 
              className="text-lg text-gray-600 max-w-3xl mx-auto"
              data-anim="fade-up"
              data-delay="100"
            >
              Collaborating with industry leaders to deliver exceptional talent solutions.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-anim="list-stagger" data-stagger="100">
            {partnerships.map((partner, index) => (
              <Card 
                key={index} 
                className="hover:shadow-xl hover:border-blue-500/50 transition-all border-none duration-300"
              >
                <CardContent className="p-8 text-center">
                  <div className="w-32 h-32 mx-auto mb-6 flex items-center justify-center bg-white rounded-lg shadow-md">
                    <img 
                      src={partner.logo} 
                      alt={partner.name}
                      className="max-w-full max-h-full object-contain p-4"
                      loading="lazy"
                    />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{partner.name}</h3>
                  <p className="text-gray-600">{partner.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
